/* eslint-disable react/jsx-no-useless-fragment */
import React, { memo, useCallback, useMemo, useRef, useState } from "react";

import {
  MessageThreadFragment,
  MessageThreadType,
  SearchFilterType,
} from "gql/graphql";
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Stack,
  InputRightElement,
  Button,
  VStack,
  HStack,
  Spacer,
  DrawerBody,
  Spinner,
  Center,
} from "@chakra-ui/react";
import colors from "app/colors";
import {
  ChatEllipsisIcon,
  CloseIcon,
  PencilOutlineIcon,
  NoSearchResultsIcon,
  SearchIcon,
  CheckPlusIcon,
} from "modules/icons";
import { forceToArray } from "modules/utils/Functions";
import {
  DEFAULT_ALL_PINNED_SEARCH_FILTERS,
  DEFAULT_RECENT_SEARCH_FILTERS,
} from "modules/utils/Constants";
import { GroupedVirtuoso } from "react-virtuoso";
import ForwardToMessageThread from "./forward-to-message-thread";
import useMessageThreadSearch from "modules/hooks/use-message-thread-search";

interface IProps {
  onForwardToSelectedMessageThread: (
    messageThread: MessageThreadFragment,
  ) => void;
  onForwardToNewMessageThread: (type: MessageThreadType) => () => void;
}

const ForwardToSelectMessageThread = ({
  onForwardToSelectedMessageThread,
  onForwardToNewMessageThread,
}: IProps) => {
  const [search, setSearch] = useState("");
  const [currentSearch, setCurrentSearch] = useState<string>();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { messageThreadSearches: pinnedMessageThreads } =
    useMessageThreadSearch({
      searchFilters: [
        ...forceToArray(DEFAULT_ALL_PINNED_SEARCH_FILTERS),
        {
          type: SearchFilterType.HasAccess,
        },
        {
          type: SearchFilterType.CanCreateMessage,
        },
      ],
      skip: !!currentSearch,
    });

  const { messageThreadSearches: recentMessageThreads } =
    useMessageThreadSearch({
      searchFilters: [
        ...forceToArray(DEFAULT_RECENT_SEARCH_FILTERS),
        {
          type: SearchFilterType.HasAccess,
        },
        {
          type: SearchFilterType.CanCreateMessage,
        },
      ],
      skip: !!currentSearch,
    });

  const {
    messageThreadSearches: searchMessageThreads,
    loading: searchLoading,
  } = useMessageThreadSearch({
    searchFilters: [
      {
        type: SearchFilterType.Text,
        stringValue: currentSearch,
      },
      {
        type: SearchFilterType.HasAccess,
      },
      {
        type: SearchFilterType.CanCreateMessage,
      },
    ],
    skip: !currentSearch,
  });

  const virtuosoGroupCounts = useMemo(() => {
    if (currentSearch) {
      return [searchMessageThreads.length];
    }
    return [pinnedMessageThreads.length, recentMessageThreads.length];
  }, [
    currentSearch,
    pinnedMessageThreads.length,
    recentMessageThreads.length,
    searchMessageThreads.length,
  ]);

  const displayNoMessages = useMemo(
    () =>
      (currentSearch && searchMessageThreads.length === 0) ||
      (!currentSearch &&
        pinnedMessageThreads.length === 0 &&
        recentMessageThreads.length === 0),
    [
      currentSearch,
      pinnedMessageThreads.length,
      recentMessageThreads.length,
      searchMessageThreads.length,
    ],
  );

  const onSearchChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    },
    [],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.stopPropagation();
        e.preventDefault();
        setCurrentSearch(search);
      }
    },
    [search],
  );

  const onClearSearchClicked = useCallback(() => {
    setSearch("");
    setCurrentSearch(undefined);
  }, []);

  const onMessageThreadClicked = useCallback(
    (messageThread: MessageThreadFragment) => () => {
      onForwardToSelectedMessageThread(messageThread);
    },
    [onForwardToSelectedMessageThread],
  );

  const renderGroupHeader = useCallback(
    (sectionIndex: number) => {
      if (currentSearch) {
        return (
          <Text
            color={colors.grayMain}
            fontSize={14}
            fontWeight={500}
            py={1}
            bg={colors.white}
          >
            SEARCH RESULTS
          </Text>
        );
      }
      if (sectionIndex === 0) {
        return (
          <Text
            bg={colors.white}
            color={colors.grayMain}
            fontSize={14}
            fontWeight={500}
            py={1}
          >
            PINNED
          </Text>
        );
      }
      return (
        <Text
          bg={colors.white}
          color={colors.grayMain}
          fontSize={14}
          fontWeight={500}
          py={1}
        >
          RECENT
        </Text>
      );
    },
    [currentSearch],
  );

  const renderMessageThread = useCallback(
    (messageThread: MessageThreadFragment) => (
      <Box
        cursor="pointer"
        _hover={{ bg: colors.whiteTertiary }}
        onClick={onMessageThreadClicked(messageThread)}
      >
        <ForwardToMessageThread messageThread={messageThread} />
      </Box>
    ),
    [onMessageThreadClicked],
  );

  const renderItem = useCallback(
    (index: number, groupindex: number) => {
      if (currentSearch) {
        return renderMessageThread(searchMessageThreads[index]);
      }
      if (groupindex === 0) {
        return renderMessageThread(pinnedMessageThreads[index]);
      }
      //The index for Grouped Virtuoso need to be adjusted since the index is
      //calculated based on the total number of items in all groups
      const recentMessageThreadIndex = index - pinnedMessageThreads.length;
      return renderMessageThread(
        recentMessageThreads[recentMessageThreadIndex],
      );
    },
    [
      currentSearch,
      pinnedMessageThreads,
      recentMessageThreads,
      renderMessageThread,
      searchMessageThreads,
    ],
  );

  return (
    <DrawerBody p={0} px={3}>
      <Stack w="full" h="full">
        <HStack
          borderBottomWidth={1}
          borderBottomColor={colors.grayPrimary}
          py={2}
          px={4}
        >
          <Button
            variant="link"
            color={colors.blue}
            fontSize={14}
            fontWeight={600}
            leftIcon={<ChatEllipsisIcon fill={colors.blue} boxSize={6} />}
            iconSpacing={1}
            onClick={onForwardToNewMessageThread(MessageThreadType.Chat)}
          >
            New Chat
          </Button>
          <Spacer />
          <Button
            variant="link"
            color={colors.orangePrimary}
            fontSize={14}
            fontWeight={600}
            leftIcon={
              <PencilOutlineIcon fill={colors.orangePrimary} boxSize={6} />
            }
            onClick={onForwardToNewMessageThread(MessageThreadType.Thread)}
          >
            New Thread
          </Button>
          <Spacer />
          <Button
            variant="link"
            color={colors.greenPrimary}
            fontSize={14}
            fontWeight={600}
            leftIcon={<CheckPlusIcon fill={colors.greenPrimary} boxSize={6} />}
            onClick={onForwardToNewMessageThread(MessageThreadType.Task)}
          >
            New Task
          </Button>
        </HStack>
        <InputGroup>
          <InputLeftElement color={colors.gray30} mx={0}>
            <SearchIcon fill={colors.gray30} boxSize={5} />
          </InputLeftElement>
          <Input
            rounded="full"
            size="md"
            w="full"
            backgroundColor={colors.whiteTertiary}
            placeholder="Search Chats & Threads"
            value={search ?? ""}
            onChange={onSearchChanged}
            onKeyDown={onKeyDown}
            color={colors.placeholderText}
          />
          <InputRightElement>
            <Button
              variant="link"
              size="sm"
              leftIcon={<CloseIcon fill={colors.gray30} boxSize={5} />}
              onClick={onClearSearchClicked}
            />
          </InputRightElement>
        </InputGroup>
        {searchLoading ? (
          <Center pt={20}>
            <Spinner size="xl" />
          </Center>
        ) : (
          <Box w="full" flex={1} ref={scrollRef} overflowY="auto" px={4}>
            {displayNoMessages ? (
              <VStack spacing={0} pt={8}>
                <NoSearchResultsIcon />
                <Text
                  color={colors.black}
                  fontSize={26}
                  fontWeight={700}
                  fontFamily="Outfit"
                >
                  No Matches Found
                </Text>
                <Text color={colors.gray50} fontSize={18} fontWeight={600}>
                  Modify search to improve results.
                </Text>
              </VStack>
            ) : (
              <GroupedVirtuoso
                groupCounts={virtuosoGroupCounts}
                groupContent={renderGroupHeader}
                itemContent={renderItem}
              />
            )}
          </Box>
        )}
      </Stack>
    </DrawerBody>
  );
};

export default memo(ForwardToSelectMessageThread);
